import { textColorFields } from '../_shared/textColor.js';
import { animationFields } from '../_shared/universal.js';
import { headingHighlightFields } from '../_shared/headingHighlight.js';
import { patternFields } from '../_shared/pattern.js';
/**
 * comparison-slider — a FROM-SCRATCH, 1:1 rebuild of Shrine Pro's "Before & After slider" section
 * (shrine-pro-ref/sections/comparison-slider.liquid). A heading (both word-HIGHLIGHT engines) + richtext +
 * an optional CTA laid out beside a draggable before/after image comparison. The slider's CSS is ported
 * VERBATIM from assets/base.css (Shrine's `rem` sizes emitted in px at 1rem=10px; `rgb(var(--color-*))`
 * triples remapped to our `--aqx-scheme-*` contract). Colors flip via ONE `data-scheme` attribute (the 6
 * Shrine schemes) or an inline custom palette — identical to the video / collage rebuilds.
 *
 * Field-type mapping (Shopify → ours): inline_richtext→richtext (Bold→highlight 1, Italic→highlight 2),
 * richtext→richtext, image_picker→image, text→text, url→link, checkbox→checkbox, select→select,
 * color→color, color_background→gradient, product→product-picker, header→group, visible_if→visibleWhen.
 * Ids are clean camelCase; labels / options / defaults stay faithful to Shrine (the 6 color schemes + custom
 * palette, the 13-pattern background, the heading-size scale, before/after labels + their own color scheme,
 * the line color + arrow style, mobile-=×0.75 padding).
 *
 * DELIBERATELY DEFERRED (Shopify-platform only, each flagged): `display_id` (Shopify "copy section id" dev
 * utility, no storefront meaning) and the ATC-to-cart wiring beyond the `[data-aqx-atc]` hook the cart
 * island already hydrates.
 */
/** Convenience for the literal hex defaults Shrine ships. */
const hex = (value) => ({ ref: 'custom', value });
const GENERAL = 'General';
const SLIDER = 'Slider';
const COLOR = 'Color';
/** The 5 Shrine scheme choices the labels / line offer (no `custom` — Shrine parity). */
const SCHEME_5 = [
    { value: 'accent-1', label: 'Accent 1' },
    { value: 'accent-2', label: 'Accent 2' },
    { value: 'background-1', label: 'Background 1' },
    { value: 'background-2', label: 'Background 2' },
    { value: 'inverse', label: 'Inverse' },
];
export const comparisonSliderSchema = {
    type: 'comparison-slider',
    name: 'Before & After slider',
    version: 1,
    templates: ['index', 'product', 'collection', 'page', 'thank-you'],
    fields: [
        // ── (top, ungrouped) ──────────────────────────────────────────────────────
        {
            id: 'visibility',
            type: 'select',
            label: 'Display on',
            default: 'always-display',
            options: [
                { value: 'always-display', label: 'All devices' },
                { value: 'mobile-hidden', label: 'Desktop only' },
                { value: 'desktop-hidden', label: 'Mobile only' },
            ],
        },
        // ── General: heading (richtext) → highlight 1 → highlight 2 → size → body + CTA ───────────────
        {
            id: 'title',
            type: 'richtext',
            label: 'Heading',
            group: GENERAL,
            default: { format: 'html', html: '<p>Before &amp; After slider</p>' },
            info: 'Apply Bold (B) to a word for highlight 1, Italic (I) for highlight 2.',
        },
        ...headingHighlightFields(1, GENERAL),
        ...headingHighlightFields(2, GENERAL),
        {
            id: 'headingSize',
            type: 'select',
            label: 'Heading size',
            group: GENERAL,
            default: 'h1',
            options: [
                { value: 'h3', label: 'Extra small' },
                { value: 'h2', label: 'Small' },
                { value: 'h1', label: 'Medium' },
                { value: 'h0', label: 'Large' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'customDesktopSize',
            type: 'range',
            label: 'Custom desktop size',
            group: GENERAL,
            min: 24,
            max: 60,
            step: 3,
            unit: 'px',
            default: 39,
            visibleWhen: { field: 'headingSize', equals: 'custom' },
        },
        {
            id: 'customMobileSize',
            type: 'range',
            label: 'Custom mobile size',
            group: GENERAL,
            min: 20,
            max: 50,
            step: 2,
            unit: 'px',
            default: 28,
            visibleWhen: { field: 'headingSize', equals: 'custom' },
        },
        {
            id: 'text',
            type: 'richtext',
            label: 'Text',
            group: GENERAL,
            default: {
                format: 'html',
                html: '<p>Highlight key differences between before and after using your product.</p>',
            },
        },
        {
            id: 'buttonLabel',
            type: 'text',
            label: 'Button label',
            group: GENERAL,
            maxLength: 60,
            info: 'Leave the label blank to hide the button.',
        },
        { id: 'link', type: 'link', label: 'Button link', group: GENERAL },
        {
            id: 'buttonStyleSecondary',
            type: 'checkbox',
            label: 'Use outline button style',
            group: GENERAL,
            default: false,
        },
        {
            id: 'atcButtonLabel',
            type: 'text',
            label: 'Add to Cart button label',
            group: GENERAL,
            maxLength: 60,
            info: 'Leave the label blank to hide the Add to Cart button.',
        },
        {
            id: 'atcProduct',
            type: 'product-picker',
            label: 'ATC Custom product',
            group: GENERAL,
            info: 'If empty, the button adds the main product from the product page to the cart (including the selected variant/quantity).',
        },
        {
            id: 'atcSkipCart',
            type: 'checkbox',
            label: 'ATC Custom product skip cart',
            group: GENERAL,
            default: false,
        },
        // ── Color: the 6 Shrine schemes + a custom palette (same engine as video / collage) ──────────
        {
            id: 'colorScheme',
            type: 'select',
            label: 'Color scheme',
            group: COLOR,
            default: 'background-1',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'background-1', label: 'Background 1' },
                { value: 'background-2', label: 'Background 2' },
                { value: 'inverse', label: 'Inverse' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'customBackground',
            type: 'color',
            label: 'Custom background color',
            group: COLOR,
            default: hex('#FFFFFF'),
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customGradientBackground',
            type: 'gradient',
            label: 'Custom gradient background',
            group: COLOR,
            info: 'If set, replaces the background color.',
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customImageBackground',
            type: 'image',
            label: 'Custom image background',
            group: COLOR,
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customMobileImageBackground',
            type: 'image',
            label: 'Custom mobile image background',
            group: COLOR,
            info: 'If empty, the desktop image is used on mobile too.',
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customText',
            type: 'color',
            label: 'Custom text color',
            group: COLOR,
            default: hex('#2E2A39'),
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customColorsSolidButtonBackground',
            type: 'color',
            label: 'Custom solid button background',
            group: COLOR,
            default: hex('#dd1d1d'),
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customColorsSolidButtonText',
            type: 'color',
            label: 'Custom solid button label color',
            group: COLOR,
            default: hex('#ffffff'),
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customColorsOutlineButton',
            type: 'color',
            label: 'Custom outline button color',
            group: COLOR,
            default: hex('#dd1d1d'),
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        // ── Background pattern (shared 13-pattern engine) ─────────────────────────────
        ...patternFields('Background pattern'),
        // ── Slider: placement + labels + line + arrows + the two images ──────────────
        {
            id: 'layout',
            type: 'select',
            label: 'Desktop slider placement',
            group: SLIDER,
            default: 'slider_second',
            options: [
                { value: 'slider_first', label: 'Slider first' },
                { value: 'slider_second', label: 'Slider second' },
            ],
        },
        { id: 'beforeLabel', type: 'text', label: 'Before label', group: SLIDER, default: 'Before' },
        { id: 'afterLabel', type: 'text', label: 'After label', group: SLIDER, default: 'After' },
        {
            id: 'labelsColorScheme',
            type: 'select',
            label: 'Labels color scheme',
            group: SLIDER,
            default: 'accent-1',
            options: SCHEME_5,
        },
        {
            id: 'lineColor',
            type: 'select',
            label: 'Separator line color',
            group: SLIDER,
            default: 'background-1',
            options: SCHEME_5,
        },
        {
            id: 'arrowsStyle',
            type: 'select',
            label: 'Arrows style',
            group: SLIDER,
            default: 'circle',
            options: [
                { value: 'classic', label: 'Classic' },
                { value: 'circle', label: 'Circle around' },
            ],
        },
        { id: 'beforeImage', type: 'image', label: 'Before image', group: SLIDER },
        { id: 'afterImage', type: 'image', label: 'After image', group: SLIDER },
        // ── Section padding (Shrine default 36 / mobile = ×0.75) ──────────────────────
        {
            id: 'paddingTop',
            type: 'range',
            label: 'Top padding',
            group: 'Section padding',
            min: 0,
            max: 100,
            step: 4,
            unit: 'px',
            default: 36,
        },
        {
            id: 'paddingBottom',
            type: 'range',
            label: 'Bottom padding',
            group: 'Section padding',
            min: 0,
            max: 100,
            step: 4,
            unit: 'px',
            default: 36,
        },
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        // The universal Animation group (2026-09-18): this section writes its own section controls
        // instead of spreading `universalFields`, so the group is spread in on its own.
        ...animationFields,
    ],
    maxBlocks: 0,
};
export const comparisonSliderPresets = [
    {
        name: 'Before & After slider',
        settings: {
            title: { format: 'html', html: '<p>Before &amp; After slider</p>' },
            headingSize: 'h1',
            headingHighlight1: 'solid-color',
            headingHighlight1Color: { ref: 'theme', token: 'accent' },
            text: {
                format: 'html',
                html: '<p>Highlight key differences between before and after using your product.</p>',
            },
            colorScheme: 'background-1',
            layout: 'slider_second',
            beforeLabel: '@t:section.before',
            afterLabel: '@t:section.after',
            labelsColorScheme: 'accent-1',
            lineColor: 'background-1',
            arrowsStyle: 'circle',
            paddingTop: 36,
            paddingBottom: 36,
        },
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const comparisonSliderMeta = {
    label: 'Before & After slider',
    group: 'Content',
    icon: 'image',
};
//# sourceMappingURL=schema.js.map